import { Avatar, AvatarImage, AvatarFallback } from "src/@/components/ui/avatar";
import { useNavigate } from "react-router";
import { Button } from "src/@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "src/@/components/ui/dropdown-menu";
import Iconify from "src/components/Iconify";
import useAuth from "src/hooks/useAuth";
import useLocales from "src/hooks/useLocales";
import { useSelector, useDispatch } from 'src/redux/store';
import { setUserStandby } from 'src/redux/slices/user';
import v2_actionEditarUsuario from 'src/actions/v2/usuarios/v2_actionEditarUsuario';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "src/@/components/ui/tooltip";

export function DropdownAccountNav({ variant = "icon" }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isUserStandby } = useSelector((state) => state.user);
  const { t, currentLang, allLangs, onChangeLang } = useLocales();

  async function handleEditStandbyUser() {
    let check = !isUserStandby;
    dispatch(setUserStandby(check));
    let data = {
      action: "update_standby_users",
      scope: { userStandby: check }
    };
    await v2_actionEditarUsuario(user.id, data);
  }

  const userInitials = (user?.nomeFantasia || user?.nome || '?').charAt(0).toUpperCase();

  const IconButton = (
    <button
      className={`h-9 w-9 flex items-center justify-center rounded-lg transition-all border outline-none focus:ring-2 relative ${
        isUserStandby
          ? 'bg-red-500/10 hover:bg-red-500/20 text-red-400 border-red-500/20 focus:ring-red-500/20'
          : 'bg-white/10 hover:bg-white/15 text-white border-white/10 focus:ring-white/20'
      }`}
    >
      <Avatar className="h-6 w-6 text-xs">
        <AvatarImage src={user?.avatar} alt={user?.nomeFantasia} />
        <AvatarFallback className="bg-violet-500/20 text-violet-300 text-xs font-semibold">{userInitials}</AvatarFallback>
      </Avatar>
      <span className="absolute top-1 right-1 flex h-2.5 w-2.5">
        <span
          className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${
            !isUserStandby ? 'bg-green-400' : 'bg-red-400'
          }`}
        ></span>
        <span
          className={`relative inline-flex rounded-full h-2.5 w-2.5 border-2 border-gray-800 ${
            !isUserStandby ? 'bg-green-500' : 'bg-red-500'
          }`}
        ></span>
      </span>
    </button>
  );

  const FullButton = (
    <button
      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all border outline-none focus:ring-2 ${
        isUserStandby
          ? 'bg-red-500/10 hover:bg-red-500/20 text-red-400 border-red-500/20 focus:ring-red-500/20'
          : 'bg-white/10 hover:bg-white/15 text-white border-white/10 focus:ring-white/20'
      }`}
    >
      <div className="relative">
        <Avatar className="h-8 w-8">
          <AvatarImage src={user?.avatar} alt={user?.nomeFantasia} />
          <AvatarFallback className="bg-violet-500/20 text-violet-300 text-sm font-semibold">{userInitials}</AvatarFallback>
        </Avatar>
        <span className="absolute -top-0.5 -right-0.5 flex h-2.5 w-2.5">
          <span
            className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${
              !isUserStandby ? 'bg-green-400' : 'bg-red-400'
            }`}
          ></span>
          <span
            className={`relative inline-flex rounded-full h-2.5 w-2.5 border-2 border-gray-800 ${
              !isUserStandby ? 'bg-green-500' : 'bg-red-500'
            }`}
          ></span>
        </span>
      </div>
      <div className="flex-1 min-w-0 text-left">
        <div className="text-sm font-medium truncate">{user?.nomeFantasia || user?.nome}</div>
        <div className="text-xs opacity-75">{isUserStandby ? t('common:status.away') : t('common:status.online')}</div>
      </div>
      <Iconify icon="lucide:chevron-right" width={16} className="opacity-50" />
    </button>
  );

  return (
    <DropdownMenu>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              {variant === "full" ? FullButton : IconButton}
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent className="bg-slate-50 text-white text-xs p-0 border-none">
            <div className="space-y-1 p-1 bg-slate-50 border rounded-md shadow-sm">
              <div className="font-semibold text-xs text-gray-900">{t('navigation:account.availabilityStatus')}</div>
              <div className="text-[10px] text-gray-500">
                {isUserStandby
                  ? t('navigation:account.awayDescription')
                  : t('navigation:account.onlineDescription')}
              </div>
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <DropdownMenuContent
        className="w-72 rounded-2xl border-gray-100 shadow-xl bg-white/95 backdrop-blur-sm"
        align="end"
      >
        <div className="flex items-center gap-3 p-2 mt-1">
          <Avatar className="h-10 w-10">
            <AvatarImage src={user?.avatar} alt={user?.nomeFantasia} />
            <AvatarFallback className="bg-violet-100 text-violet-700 font-semibold">{userInitials}</AvatarFallback>
          </Avatar>
          <div className="flex flex-col">
            <span className="text-sm font-bold text-gray-900">{user?.nomeFantasia}</span>
            <span className="text-xs text-gray-500">{user?.email}</span>
          </div>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuItem
            onClick={(e) => {
              e.preventDefault();
              handleEditStandbyUser();
            }}
          >
            <Iconify
              icon={isUserStandby ? "solar:coffee-bold-duotone" : "solar:user-check-bold-duotone"}
              className={`mr-2 h-4 w-4 ${isUserStandby ? 'text-amber-600' : 'text-green-600'}`}
            />
            <span>{isUserStandby ? t('navigation:account.goOnline') : t('navigation:account.goAway')}</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate('/dashboard/conta/perfil')}>
            <Iconify icon="ion:settings-outline" className="mr-2 h-4 w-4" />
            <span>{t('navigation:account.settings')}</span>
          </DropdownMenuItem>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <Iconify icon="lucide:languages" className="mr-2 h-4 w-4" />
              <span>{t('common:language.label')}</span>
            </DropdownMenuSubTrigger>
            <DropdownMenuPortal>
              <DropdownMenuSubContent>
                {allLangs.map((lang) => (
                  <DropdownMenuItem
                    key={lang.value}
                    onClick={() => onChangeLang(lang.value)}
                    className={currentLang.value === lang.value ? 'bg-[#7C3AED]/10 text-[#7C3AED]' : ''}
                  >
                    <span className="mr-2">{lang.icon}</span>
                    <span>{lang.label}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuPortal>
          </DropdownMenuSub>
          <DropdownMenuSeparator />
          <Button onClick={() => logout()} className="w-full mt-2 mb-2" variant="outline">
            {t('navigation:account.logout')}
          </Button>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
